import { lazy, ComponentType } from 'react';

import { logger } from 'utils/logger';

/**
 * Creates a lazy-loaded component with retry logic and exponential backoff.
 *
 * This utility wraps React.lazy() with automatic retry functionality to handle
 * temporary network failures or chunk loading errors gracefully.
 *
 * @param importFn - Dynamic import function that returns a Promise with the component
 * @param retries - Number of retry attempts (default: 3)
 * @returns React.LazyExoticComponent ready to be used in JSX
 *
 * @example
 * // Basic usage
 * const MyPage = lazyLoad(() => import('pages/MyPage'));
 *
 * @example
 * // With named export
 * const MyPage = lazyLoad(() =>
 *   import('pages/MyPage').then(module => ({ default: module.MyPage }))
 * );
 *
 * @example
 * // With custom retry count
 * const MyPage = lazyLoad(() => import('pages/MyPage'), 5);
 */
export function lazyLoad<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  retries = 3
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    let lastError: Error | undefined;

    for (let attempt = 0; attempt < retries; attempt++) {
      try {
        return await importFn();
      } catch (error) {
        lastError = error as Error;

        // Log retry attempt in development
        if (process.env.NODE_ENV === 'development') {
          logger.warn(
            `[lazyLoad] Attempt ${attempt + 1}/${retries} failed for chunk. Retrying...`,
            error
          );
        }

        // Exponential backoff: 1s, 2s, 4s, 8s...
        await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, attempt)));
      }
    }

    // All retries exhausted - throw the last error
    logger.error(`[lazyLoad] Failed to load chunk after ${retries} attempts`, lastError);
    throw lastError || new Error(`Failed to load chunk after ${retries} attempts`);
  });
}

/**
 * Creates a lazy-loaded component with named export support.
 *
 * @param importFn - Dynamic import function
 * @param exportName - Name of the export to use (for named exports)
 * @param retries - Number of retry attempts
 *
 * @example
 * const { Login } = lazyLoadNamed(() => import('pages/Login'), 'Login');
 */
export function lazyLoadNamed<T extends ComponentType<any>>(
  importFn: () => Promise<Record<string, T>>,
  exportName: string,
  retries = 3
): React.LazyExoticComponent<T> {
  return lazyLoad(() => importFn().then(module => ({ default: module[exportName] as T })), retries);
}

export default lazyLoad;
